/**
 * TabWriteup — 350-word editorial writeup that sits at the top of each
 * University detail tab. Fetches /api/universities/{id}/writeup/{tab}
 * lazily on tab mount. When no writeup exists yet (LLM daemon hasn't
 * reached this uni), it renders a graceful placeholder rather than
 * blocking the tab.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, Clock } from "lucide-react";

const TAB_META = {
  admission: { label: "Admissions advice", accent: "#92400E" },
  course:    { label: "Courses advice",    accent: "#7C3AED" },
  cost:      { label: "Cost advice",       accent: "#0E7490" },
  outcomes:  { label: "Outcomes advice",   accent: "#166534" },
  life:      { label: "Student-life advice", accent: "#B45309" },
  teaching:  { label: "Teaching advice",   accent: "#4338CA" },
  overview:  { label: "Overview",          accent: "#002147" },
};

/**
 * Split a paragraph on markdown-style **bold** markers and return an
 * array of React children. We deliberately support ONLY the **bold**
 * markdown token — the prompt disallows everything else. This avoids
 * needing a full markdown parser.
 */
function renderInlineBold(text) {
  // Non-greedy match — allows literal `*` inside the bolded phrase
  // (e.g. `**A*A*A**` for A-level grades).
  const parts = text.split(/(\*\*.+?\*\*)/g);
  return parts.map((seg, i) => {
    if (seg.startsWith("**") && seg.endsWith("**")) {
      return <strong key={i} className="font-semibold text-stone-900">{seg.slice(2, -2)}</strong>;
    }
    return <span key={i}>{seg}</span>;
  });
}

export default function TabWriteup({ uniId, tab }) {
  const [state, setState] = useState({ loading: true, text: null, missing: false });

  useEffect(() => {
    if (!uniId || !tab) return;
    let cancelled = false;
    setState({ loading: true, text: null, missing: false });
    api
      .get(`/universities/${encodeURIComponent(uniId)}/writeup/${tab}`)
      .then((r) => {
        if (cancelled) return;
        const t = r.data?.writeup?.text || "";
        setState({ loading: false, text: t, missing: !t });
      })
      .catch((e) => {
        if (cancelled) return;
        const is404 = e?.response?.status === 404;
        setState({ loading: false, text: null, missing: is404 });
      });
    return () => { cancelled = true; };
  }, [uniId, tab]);

  const meta = TAB_META[tab] || TAB_META.overview;

  if (state.loading) {
    return (
      <div
        data-testid={`tab-writeup-${tab}`}
        className="mb-8 lg:mb-0"
      >
        <div className="h-3 w-32 bg-[#002147]/10 rounded animate-pulse" />
        <div className="mt-4 space-y-2">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-3 bg-[#002147]/5 rounded animate-pulse" style={{ width: `${85 - i * 4}%` }} />
          ))}
        </div>
      </div>
    );
  }

  if (state.missing || !state.text) {
    return (
      <div
        data-testid={`tab-writeup-${tab}-missing`}
        className="mb-8 lg:mb-0 border-l-2 border-dashed border-[#002147]/20 pl-5"
      >
        <div className="flex items-start gap-2">
          <Clock className="h-4 w-4 mt-0.5" style={{ color: meta.accent }} />
          <div>
            <div
              className="text-[11px] uppercase tracking-[0.2em] font-mono font-semibold"
              style={{ color: meta.accent }}
            >
              {meta.label}
            </div>
            <p className="mt-3 font-serif text-base leading-relaxed text-[#002147]/70">
              Our editorial team hasn't published this section for this
              university yet — it's queued in the nightly writeup pass.
              The structured data on this tab is complete and accurate;
              come back in a day or two for the guided advice.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={`tab-writeup-${tab}`}
      className="mb-8 lg:mb-0"
    >
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="h-3.5 w-3.5" style={{ color: meta.accent }} />
        <div
          className="text-[11px] uppercase tracking-[0.2em] font-mono font-semibold"
          style={{ color: meta.accent }}
        >
          {meta.label}
        </div>
      </div>
      <div className="font-serif text-[17px] leading-[1.7] text-[#002147]/90 space-y-4">
        {state.text
          .split(/\n\s*\n/)
          .map((p) => p.trim())
          .filter(Boolean)
          .map((para, i) => (
            <p key={i}>{renderInlineBold(para)}</p>
          ))}
      </div>
      <div className="mt-6 pt-4 border-t border-[#002147]/10 text-[10px] font-underground-sc tracking-widest text-[#002147]/40">
        Editorial · grounded in verified facts
      </div>
    </div>
  );
}
